import { Link } from 'react-router-dom';
import SEOHead from '../../components/feature/SEOHead';

interface SitemapLink {
  title: string;
  url: string;
  description: string;
  icon: string;
}

interface SitemapSection {
  title: string;
  links: SitemapLink[];
}

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const mapaDoSiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Mapa do Site | Carta do Eu Futuro',
  url: `${SITE_URL}/mapa-do-site`,
  description: 'Navegue por todas as páginas do site Carta do Eu Futuro: exercícios gratuitos de PNL, worksheets, blog, planos, comunidade e muito mais.',
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Início', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Mapa do Site', item: `${SITE_URL}/mapa-do-site` },
    ],
  },
};

export default function MapaDoSite() {
  const sections: SitemapSection[] = [
    {
      title: 'Início',
      links: [
        {
          title: 'Página Inicial',
          url: '/',
          description: 'Conheça o método de transformação pessoal da Bia Holmes',
          icon: 'ri-home-4-line'
        },
        {
          title: 'Sobre Bia Holmes',
          url: '/sobre',
          description: 'História, missão e propósito da criadora do método',
          icon: 'ri-user-heart-line'
        }
      ]
    },
    {
      title: 'Conteúdo & Recursos',
      links: [
        {
          title: 'Blog',
          url: '/blog',
          description: 'Artigos sobre desenvolvimento pessoal, PNL e transformação',
          icon: 'ri-article-line'
        },
        {
          title: 'Quiz de Autoconhecimento',
          url: '/quiz',
          description: 'Descubra seu perfil de transformação pessoal',
          icon: 'ri-questionnaire-line'
        },
        {
          title: 'Depoimentos',
          url: '/depoimentos',
          description: 'Histórias reais de transformação dos nossos membros',
          icon: 'ri-chat-quote-line'
        },
        {
          title: 'Galeria de Cartas',
          url: '/galeria',
          description: 'Veja cartas inspiradoras escritas pela comunidade',
          icon: 'ri-gallery-line'
        },
        {
          title: 'Vitórias da Comunidade',
          url: '/vitorias',
          description: 'Celebre as conquistas dos membros',
          icon: 'ri-trophy-line'
        },
        {
          title: 'FAQ - Perguntas Frequentes',
          url: '/faq',
          description: 'Tire suas dúvidas sobre o método e planos',
          icon: 'ri-question-answer-line'
        }
      ]
    },
    {
      title: 'Exercícios Gratuitos de PNL',
      links: [
        {
          title: 'Ancoragem Emocional',
          url: '/exercicio-gratis/ancoragem-emocional',
          description: 'Técnica de PNL para acessar estados emocionais positivos',
          icon: 'ri-anchor-line'
        },
        {
          title: 'Linha do Tempo PNL',
          url: '/exercicio-gratis/linha-tempo-pnl',
          description: 'Ressignifique seu passado e crie seu futuro ideal',
          icon: 'ri-time-line'
        },
        {
          title: 'Ressignificação (Reframing)',
          url: '/exercicio-gratis/ressignificacao-reframing-pnl',
          description: 'Transforme crenças limitantes em empoderadoras',
          icon: 'ri-refresh-line'
        },
        {
          title: 'Submodalidades PNL',
          url: '/exercicio-gratis/submodalidades-pnl',
          description: 'Modifique a intensidade de memórias e emoções',
          icon: 'ri-contrast-2-line'
        },
        {
          title: 'Técnica Swish PNL',
          url: '/exercicio-gratis/tecnica-swish-pnl',
          description: 'Substitua comportamentos indesejados por novos padrões',
          icon: 'ri-arrow-left-right-line'
        },
        {
          title: 'Modelagem de Excelência',
          url: '/exercicio-gratis/modelagem-excelencia-pnl',
          description: 'Aprenda a modelar pessoas de sucesso',
          icon: 'ri-star-line'
        },
        {
          title: 'Identidade de Alta Performance',
          url: '/exercicio-gratis/identidade-alta-performance',
          description: 'Construa sua identidade de sucesso',
          icon: 'ri-user-star-line'
        },
        {
          title: 'Propósito de Vida',
          url: '/exercicio-gratis/proposito-vida',
          description: 'Descubra seu propósito e missão de vida',
          icon: 'ri-compass-3-line'
        },
        {
          title: 'Amor Ideal',
          url: '/exercicio-gratis/amor-ideal',
          description: 'Atraia o relacionamento dos seus sonhos',
          icon: 'ri-heart-3-line'
        },
        {
          title: 'Prosperidade e Carreira',
          url: '/exercicio-gratis/prosperidade-carreira',
          description: 'Manifeste abundância profissional e financeira',
          icon: 'ri-money-dollar-circle-line'
        },
        {
          title: 'Saúde e Bem-Estar',
          url: '/exercicio-gratis/saude-bem-estar',
          description: 'Crie hábitos saudáveis e energia vital',
          icon: 'ri-heart-pulse-line'
        }
      ]
    },
    {
      title: 'Worksheets Interativos',
      links: [
        {
          title: 'Worksheet: Encantations',
          url: '/worksheet-encantations',
          description: 'Crie suas afirmações poderosas com movimento',
          icon: 'ri-magic-line'
        },
        {
          title: 'Worksheet: Linha do Tempo',
          url: '/worksheet-linha-tempo',
          description: 'Guia completo da técnica de linha do tempo',
          icon: 'ri-calendar-line'
        },
        {
          title: 'Worksheet: Ressignificação',
          url: '/worksheet-ressignificacao',
          description: 'Transforme crenças limitantes passo a passo',
          icon: 'ri-loop-left-line'
        },
        {
          title: 'Worksheet: Submodalidades',
          url: '/worksheet-submodalidades',
          description: 'Modifique suas representações internas',
          icon: 'ri-settings-3-line'
        },
        {
          title: 'Worksheet: Técnica Swish',
          url: '/worksheet-swish',
          description: 'Substitua padrões mentais indesejados',
          icon: 'ri-arrow-left-right-fill'
        }
      ]
    },
    {
      title: 'Planos & Assinaturas',
      links: [
        {
          title: 'Planos e Preços',
          url: '/planos',
          description: 'Escolha o plano ideal para sua jornada de transformação',
          icon: 'ri-price-tag-3-line'
        },
        {
          title: 'Caderno da Gratidão',
          url: '/gratidao',
          description: 'Pratique gratidão diária e transforme sua mentalidade',
          icon: 'ri-book-open-line'
        },
        {
          title: 'Programa Diário',
          url: '/programa-diario',
          description: 'Rotina matinal de alta performance',
          icon: 'ri-sun-line'
        }
      ]
    },
    {
      title: 'Comunidade & Parcerias',
      links: [
        {
          title: 'Comunidade',
          url: '/comunidade',
          description: 'Conecte-se com outros membros em transformação',
          icon: 'ri-team-line'
        },
        {
          title: 'Programa de Influencers',
          url: '/influencer',
          description: 'Seja um parceiro e ganhe comissões',
          icon: 'ri-user-voice-line'
        },
        {
          title: 'Imprensa',
          url: '/imprensa',
          description: 'Kit de mídia e informações para jornalistas',
          icon: 'ri-newspaper-line'
        }
      ]
    },
    {
      title: 'Legal & Privacidade',
      links: [
        {
          title: 'Termos de Uso',
          url: '/termos',
          description: 'Termos e condições de uso da plataforma',
          icon: 'ri-file-text-line'
        },
        {
          title: 'Política de Privacidade',
          url: '/privacidade',
          description: 'Como protegemos seus dados pessoais',
          icon: 'ri-shield-check-line'
        }
      ]
    }
  ];

  return (
    <>
      <SEOHead
        title="Mapa do Site — Todas as Páginas | Carta do Eu Futuro"
        description="Navegue por todas as páginas do site Carta do Eu Futuro: exercícios gratuitos de PNL, worksheets, blog, planos, comunidade e muito mais."
        keywords="mapa do site, sitemap, navegação, índice de páginas, exercícios PNL, worksheets, blog desenvolvimento pessoal"
        canonical="/mapa-do-site"
        ogImage={`${SITE_URL}/og-sitemap.png`}
        schema={mapaDoSiteSchema}
      />

      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-teal-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 transition-colors mb-4 cursor-pointer"
            >
              <i className="ri-arrow-left-line text-lg"></i>
              <span className="text-sm font-medium">Voltar para o início</span>
            </Link>
            <h1 className="text-4xl font-bold text-gray-900 mb-3">
              Mapa do Site
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl">
              Navegue por todas as páginas e recursos disponíveis na plataforma Bia Holmes. Encontre exercícios, conteúdos e ferramentas para sua jornada de transformação.
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="space-y-12">
            {sections.map((section, sectionIndex) => (
              <div key={sectionIndex} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <div className="w-1 h-8 bg-gradient-to-b from-teal-500 to-amber-500 rounded-full"></div>
                  {section.title}
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {section.links.map((link, linkIndex) => (
                    <Link
                      key={linkIndex}
                      to={link.url}
                      className="group flex items-start gap-4 p-4 rounded-xl border border-gray-200 hover:border-teal-300 hover:bg-teal-50/50 transition-all cursor-pointer"
                    >
                      <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-teal-500 to-amber-500 rounded-lg text-white flex-shrink-0 group-hover:scale-110 transition-transform">
                        <i className={`${link.icon} text-lg`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-teal-600 transition-colors">
                          {link.title}
                        </h3>
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {link.description}
                        </p>
                      </div>
                      <i className="ri-arrow-right-line text-gray-400 group-hover:text-teal-600 group-hover:translate-x-1 transition-all flex-shrink-0"></i>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Footer Info */}
          <div className="mt-12 bg-gradient-to-r from-teal-500 to-amber-500 rounded-2xl p-8 text-white text-center">
            <i className="ri-map-pin-line text-4xl mb-4"></i>
            <h3 className="text-2xl font-bold mb-2">Não encontrou o que procura?</h3>
            <p className="text-teal-50 mb-6 max-w-2xl mx-auto">
              Entre em contato conosco ou visite nossa página de FAQ para mais informações sobre a plataforma e nossos recursos.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/faq"
                className="px-6 py-3 bg-white text-teal-600 rounded-lg font-semibold hover:bg-teal-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Ver FAQ
              </Link>
              <Link
                to="/"
                className="px-6 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Voltar ao Início
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
